import { getAuthToken } from './tokenService';
import { getApiBaseUrl } from './api/config';

export interface ChangeEvent {
  type: string;
  actorUserId: string;
  actorDisplayName?: string;
  albumId?: string;
  photoId?: string;
  entityId?: string;
  timestamp: string;
}

export const ChangeEventTypes = {
  AlbumCreated: 'album.created',
  AlbumUpdated: 'album.updated',
  AlbumDeleted: 'album.deleted',
  PhotoAdded: 'photo.added',
  PhotoDeleted: 'photo.deleted',
  PhotoReordered: 'photo.reordered',
  PhotoUpdated: 'photo.updated',
  CommentAdded: 'comment.added',
  CommentDeleted: 'comment.deleted',
  CommentPinned: 'comment.pinned',
  CommentUnpinned: 'comment.unpinned',
  ReactionAdded: 'reaction.added',
  ReactionRemoved: 'reaction.removed',
  FriendshipCreated: 'friendship.created',
  FriendshipRemoved: 'friendship.removed',
} as const;

type Listener = (event: ChangeEvent) => void;

let abortController: AbortController | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = 1000;
const MAX_RECONNECT_DELAY = 30000;
const listeners = new Set<Listener>();

export function onChangeEvent(callback: Listener): () => void {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function notifyListeners(event: ChangeEvent) {
  listeners.forEach(cb => {
    try { cb(event); } catch { /* listener error ignored */ }
  });
}

async function readStream(response: Response) {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Process complete SSE messages (separated by double newline)
    const messages = buffer.split('\n\n');
    buffer = messages.pop() || '';

    for (const message of messages) {
      if (!message.trim()) continue;

      // Parse SSE data lines
      for (const line of message.split('\n')) {
        if (line.startsWith('data: ')) {
          try {
            const event: ChangeEvent = JSON.parse(line.slice(6));
            notifyListeners(event);
          } catch {
            // Malformed JSON — skip
          }
        }
        // comment lines (: heartbeat) are intentionally ignored
      }
    }
  }
}

async function connect() {
  const token = await getAuthToken();
  if (!token) return;

  abortController = new AbortController();

  try {
    const url = `${getApiBaseUrl()}/api/notifications/stream`;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      signal: abortController.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`SSE connection failed: ${response.status}`);
    }

    // Connection succeeded — reset backoff
    reconnectDelay = 1000;

    await readStream(response);
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return; // Intentional disconnect
    }
    // eslint-disable-next-line no-console
    console.warn('SSE connection error, will reconnect:', err);
  }

  // Stream ended or errored — schedule reconnect
  scheduleReconnect();
}

function scheduleReconnect() {
  if (abortController?.signal.aborted) return; // Was intentionally disconnected
  reconnectTimeout = setTimeout(() => {
    connect();
  }, reconnectDelay);
  reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
}

export function connectNotifications(): void {
  disconnectNotifications();
  connect();
}

export function disconnectNotifications(): void {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
  reconnectDelay = 1000;
}
